import type { Clause } from './ClauseBuilder'
import type { VocabularyEntry } from './api'

/**
 * Quick filters: the common questions, one click each.
 *
 * WHY THIS EXISTS. The clause builder can express anything the engine can
 * evaluate, and that turned out to be the problem — it asks you to pick a
 * name, then an operator, then a value, which is three decisions before you
 * have said anything. Powerful and complex. v1 had the other half and v2
 * shipped without it: curated chips grouped under the question they answer, so
 * "free accounts that have not been back in a year" is two clicks instead of
 * six dropdowns.
 *
 * Restored here, adapted to v2's vocabulary — and adapted in one important
 * way. In v1 the chips and the advanced builder were two states folded
 * together into one spec, which meant two places could disagree about the same
 * field. Here A CHIP IS A SHORTCUT INTO THE CLAUSE LIST ITSELF: selecting one
 * adds exactly the clause it names, and that clause then shows up in Advanced
 * where it can be read and adjusted. One source of truth, and no fold.
 *
 * A chip therefore carries a whole clause, spelled out rather than assembled,
 * so what it does is reviewable in one place. Nothing here evaluates anything.
 */

export type FacetOption = {
  label: string
  /** The clause this chip means. `values` may hold several for a `in` chip. */
  clause: Clause
  hint?: string
}

export type Facet = {
  id: string
  /** The row label. Empty for a row of independent yes/no chips. */
  label: string
  /**
   * 'one' — either/or, with an implicit `any`.
   * 'many' — combine values.
   * 'range' — two calendar dates on `field`, which is a PAIR of clauses
   *   rather than a chip: `field >= from` and `field <= to`, either half
   *   optional so an open-ended range works.
   */
  kind: 'one' | 'many' | 'range'
  options: FacetOption[]
  /** 'range' only: the date signal both bounds are on. */
  field?: string
  hint?: string
}

export type FacetGroup = { title: string; facets: Facet[] }

/** `commercial_state == paying`. */
const band = (name: string, value: string): Clause => ({
  kind: 'band',
  name,
  op: '==',
  values: [value],
})

/** `has_verified_email` / `!has_verified_email`. */
const flag = (name: string, on = true): Clause => ({
  kind: 'flag',
  name,
  negated: !on,
})

/** `days_since_visit <= 30`. */
const within = (name: string, days: number): Clause => ({
  kind: 'number',
  name,
  op: '<=',
  bound: days,
})

/** `days_since_visit > 365`. */
const longerThan = (name: string, days: number): Clause => ({
  kind: 'number',
  name,
  op: '>',
  bound: days,
})

/**
 * The three states a banner-config setting has, and `unset` is not `off`.
 *
 * Every published banner has a config document, but not every KEY: the schema
 * grew over time, so a banner saved before a feature existed simply has no
 * entry for it — gpcSupportMode is missing on 208,536 of 243,752 rows.
 *
 * That distinction is the whole point of this section. `Off` is a customer who
 * switched something off and could switch it back on. `Not in config` is a
 * banner that predates the feature, which is an upgrade conversation and a
 * different email. Folding them together would tell 208,536 accounts they had
 * declined GPC.
 */
const SETTING = (
  name: string,
  hints: { on?: string; off?: string } = {},
): FacetOption[] => [
  { label: 'On', clause: band(name, 'on'), hint: hints.on },
  {
    label: 'Off',
    clause: band(name, 'off'),
    hint: hints.off ?? 'in their config and switched off — they could switch it on',
  },
  {
    label: 'Not in config',
    clause: band(name, 'unset'),
    hint: 'their banner predates this setting — an upgrade, not a decision',
  },
]

/** One regulation switch, on the same three states. */
const REGULATION = (name: string, hint?: string): FacetOption[] => [
  { label: 'On', clause: band(name, 'on'), hint },
  { label: 'Off', clause: band(name, 'off') },
  {
    label: 'Not in config',
    clause: band(name, 'unset'),
    hint: 'the older config shape had no switch for this regulation',
  },
]

/**
 * The three states every banner-config setting has.
 *
 * "NOT REPORTED" IS A CHIP, not an absence, and that is the whole reason these
 * are bands rather than yes/no flags. Only 15% of live apps have ever posted a
 * configuration (41,558 of 274,928, measured 2026-09-07), so the unreported
 * majority is the normal answer — and a two-state control would have folded it
 * into "off", which says the customer switched something off when the truth is
 * that we have never heard from them.
 */
const CONFIG_OPTIONS = (
  name: string,
  hints: { on?: string; off?: string; unknown?: string } = {},
): FacetOption[] => [
  { label: 'On', clause: band(name, 'on'), hint: hints.on },
  {
    label: 'Off',
    clause: band(name, 'off'),
    hint: hints.off ?? 'reported a configuration, and this setting was not in it',
  },
  {
    label: 'Not reported',
    clause: band(name, 'unknown'),
    hint: hints.unknown ?? 'the banner has never posted a configuration — 85% of live apps',
  },
]

/**
 * A calendar date range on one signal.
 *
 * `options` is empty on purpose: a range is not a set of chips, it is two date
 * boxes, and the row renders them from `field`. It sits beside the period
 * chips rather than replacing them because they answer different questions —
 * "signed up in the last 30 days" follows today around, "signed up between
 * January and June" does not.
 */
const range = (id: string, label: string, field: string, hint?: string): Facet => ({
  id,
  label,
  kind: 'range',
  field,
  options: [],
  hint,
})

/**
 * The periods every date row offers.
 *
 * "A week, a month, three months, a year" — and `Longer ago` on the far end,
 * because "not been back in over a year" is the question people actually ask
 * and it is the one a `within` chip cannot express.
 */
const AGE_OPTIONS = (name: string): FacetOption[] => [
  { label: 'Last 7 days', clause: within(name, 7) },
  { label: 'Last 30 days', clause: within(name, 30) },
  { label: 'Last 3 months', clause: within(name, 90) },
  { label: 'Last year', clause: within(name, 365) },
  { label: 'Over a year ago', clause: longerThan(name, 365) },
]

export const FACET_GROUPS: FacetGroup[] = [
  {
    title: 'Paying or free?',
    facets: [
      {
        id: 'commercial',
        label: 'Subscription',
        kind: 'one',
        options: [
          { label: 'Paying', clause: band('commercial_state', 'paying') },
          { label: 'Churned', clause: band('commercial_state', 'churned') },
          { label: 'Never paid', clause: band('commercial_state', 'never_paid') },
        ],
      },
      {
        id: 'retention',
        label: '',
        kind: 'many',
        options: [
          {
            label: 'Cancelled, still served',
            clause: flag('cancelled_but_still_served'),
            hint: 'they pressed cancel and the paid period has not ended — there is still time',
          },
          { label: 'Has a discount', clause: flag('has_discount') },
          { label: 'Billed manually', clause: flag('is_manual_billing') },
        ],
      },
      {
        id: 'renewal',
        label: 'Renews in',
        kind: 'one',
        options: [
          { label: '7 days', clause: within('days_to_renewal', 7) },
          { label: '30 days', clause: within('days_to_renewal', 30) },
          { label: '90 days', clause: within('days_to_renewal', 90) },
        ],
        hint: 'paid accounts only — an account with no renewal date on record matches none of these',
      },
      range('renewal_between', 'Renews between', 'renewal_on'),
      {
        id: 'churn_reason',
        label: 'Left because',
        kind: 'many',
        options: [
          { label: 'Went to a competitor', clause: band('churn_reason', 'competitive') },
          { label: 'Product gap', clause: band('churn_reason', 'product_gap') },
          { label: 'No longer needed', clause: band('churn_reason', 'natural') },
          { label: 'Price or other', clause: band('churn_reason', 'price_or_other') },
          { label: 'Never asked', clause: band('churn_reason', 'unknown') },
        ],
        hint: 'the exit survey is optional, so "never asked" is the largest of these',
      },
    ],
  },
  {
    title: 'Can we reach them?',
    facets: [
      {
        id: 'verified',
        label: 'Email verified',
        kind: 'one',
        options: [
          { label: 'Yes', clause: flag('has_verified_email') },
          {
            label: 'No',
            clause: flag('has_verified_email', false),
            hint: 'about 55% of the base — they cannot sign in to act on anything',
          },
        ],
      },
      {
        id: 'identity',
        label: '',
        kind: 'many',
        options: [
          {
            label: 'Owns the domain',
            clause: flag('has_own_domain_email'),
            hint: 'a match is a signal; a non-match means nothing at all',
          },
          { label: 'Has a company name', clause: flag('has_business_identity') },
          { label: 'More than one login', clause: flag('has_team') },
        ],
      },
      {
        id: 'quota_told',
        label: 'Quota warning',
        kind: 'one',
        options: [
          { label: 'Not sent yet', clause: flag('already_told_about_quota', false) },
          { label: 'Already sent', clause: flag('already_told_about_quota') },
        ],
        hint: 'the platform may have emailed them about the cap already — saying it twice annoys',
      },
    ],
  },
  {
    title: 'How far did they get?',
    facets: [
      {
        id: 'funnel',
        label: 'Stalled at',
        kind: 'many',
        options: [
          { label: 'Not installed', clause: band('funnel_stage', 'not_installed') },
          { label: 'Banner, no consent', clause: band('funnel_stage', 'silent_banner') },
          { label: 'Recording consents', clause: band('funnel_stage', 'activated') },
          { label: 'Subscribed', clause: band('funnel_stage', 'subscribed') },
        ],
        hint: 'pick several — an account at any of them counts',
      },
      {
        id: 'activated',
        label: 'Ever recorded a consent',
        kind: 'one',
        options: [
          { label: 'Yes', clause: flag('ever_activated') },
          {
            label: 'Never',
            clause: flag('ever_activated', false),
            hint: 'separates "it worked and stopped" from "it never worked" — a different message',
          },
        ],
      },
      {
        id: 'more_sites',
        label: 'More than one site',
        kind: 'one',
        options: [
          { label: 'Yes', clause: flag('has_multiple_sites') },
          { label: 'Just one', clause: flag('has_multiple_sites', false) },
        ],
      },
      {
        id: 'first_consent',
        label: 'First consent',
        kind: 'one',
        options: AGE_OPTIONS('days_since_first_consent'),
      },
      range('first_consent_between', 'First consent between', 'first_consent_on'),
      range('customized_between', 'Banner customised between', 'banner_customized_on'),
      {
        id: 'investment',
        label: 'Banner',
        kind: 'one',
        options: [
          { label: 'Customised', clause: band('investment', 'configured') },
          { label: 'Left as default', clause: band('investment', 'default') },
        ],
      },
    ],
  },
  {
    title: 'Have they been around?',
    facets: [
      // TWO WAYS TO ASK ABOUT RECENCY, on two different names, and they are
      // deliberately separate rows. `engagement_recency` is the band ladder
      // with cut-offs documented in variables.yaml; `days_since_visit` is the
      // same column with a window you choose. Mixing them into one row put
      // `never` and a day-bound in the same either/or, so picking "Last 7
      // days" silently cleared a band selection on a different name.
      {
        id: 'recency_band',
        label: 'Last seen',
        kind: 'many',
        options: [
          { label: 'Active', clause: band('engagement_recency', 'active'), hint: 'under 30 days' },
          { label: 'Cooling', clause: band('engagement_recency', 'cooling'), hint: '30–89 days' },
          { label: 'Dormant', clause: band('engagement_recency', 'dormant'), hint: '90–364 days' },
          { label: 'Gone', clause: band('engagement_recency', 'gone'), hint: 'a year or more' },
          {
            label: 'Never',
            clause: band('engagement_recency', 'never'),
            hint: 'never opened it at all — not the far end of the scale, its own state',
          },
        ],
        hint: 'the documented cut-offs; use the row below for a window of your own',
      },
      {
        id: 'dashboard',
        label: 'Dashboard opened',
        kind: 'one',
        options: AGE_OPTIONS('days_since_visit'),
      },
      {
        id: 'signed_up',
        label: 'Signed up',
        kind: 'one',
        options: AGE_OPTIONS('days_since_account_created'),
      },
      range(
        'signed_up_between',
        'Signed up between',
        'account_created_on',
        'a fixed range — unlike the row above it, it does not move as time passes',
      ),
      {
        id: 'company_age',
        label: 'Company created',
        kind: 'one',
        options: AGE_OPTIONS('days_since_company_created'),
      },
      range('company_between', 'Company created between', 'company_created_on'),
      range('updated_between', 'Record changed between', 'account_updated_on'),
    ],
  },
  {
    title: 'What is their banner seeing?',
    facets: [
      {
        id: 'traffic',
        label: 'Traffic',
        kind: 'many',
        options: [
          { label: 'Dead', clause: band('traffic_band', 'dead') },
          { label: 'Low', clause: band('traffic_band', 'low') },
          { label: 'Medium', clause: band('traffic_band', 'medium') },
          { label: 'High', clause: band('traffic_band', 'high') },
        ],
      },
      {
        id: 'reporting',
        label: 'Reporting',
        kind: 'one',
        options: [
          {
            label: 'Reports figures',
            clause: flag('traffic_reported'),
            hint: 'required before any copy may claim what their banner recorded',
          },
          {
            label: 'Never reported',
            clause: flag('traffic_reported', false),
            hint: 'our telemetry gap, not their silence — do not tell them their site is quiet',
          },
        ],
      },
      // EITHER/OR ROWS, not a pile of chips, for the three facts whose
      // negative is a real and different question. "Some visitors arrived" is
      // `!no_visits_recorded`, and every seeded condition that says so had to
      // use the advanced builder because only the positive had a chip.
      {
        id: 'visitors',
        label: 'Visitors',
        kind: 'one',
        options: [
          { label: 'Some arrived', clause: flag('no_visits_recorded', false) },
          {
            label: 'Nobody arrived',
            clause: flag('no_visits_recorded'),
            hint: 'reports, and reports zero — a banner recording nothing on nobody is working',
          },
        ],
      },
      {
        id: 'consents',
        label: 'Consents',
        kind: 'one',
        options: [
          { label: 'Some recorded', clause: flag('no_consents_recorded', false) },
          { label: 'None recorded', clause: flag('no_consents_recorded') },
        ],
      },
      {
        id: 'behaviour',
        label: '',
        kind: 'many',
        options: [
          { label: 'Notable blocking', clause: flag('has_notable_blocking') },
          { label: 'Mostly rejected', clause: flag('has_high_reject_rate') },
          { label: 'Mostly accepted', clause: flag('has_high_accept_rate') },
          { label: 'Has EU visitors', clause: flag('has_eu_visitors') },
        ],
      },
      {
        id: 'quota',
        label: 'Quota',
        kind: 'many',
        options: [
          { label: 'Over', clause: band('quota_pressure', 'over') },
          { label: 'Approaching', clause: band('quota_pressure', 'approaching') },
          { label: 'Comfortable', clause: band('quota_pressure', 'comfortable') },
          { label: 'No cap', clause: band('quota_pressure', 'none') },
        ],
      },
    ],
  },
  {
    // THE BANNER CONFIG WE HOLD, from UserDesignLive.BannerConfigJSON — as
    // distinct from the plugin's self-report further down. This is our own
    // record, and every published banner has one.
    title: 'How is the banner configured?',
    facets: [
      {
        id: 'gpc_support',
        label: 'GPC support',
        kind: 'one',
        options: SETTING('gpc_support', {
          on: 'honours the Global Privacy Control signal — only 763 banners in the whole base',
        }),
      },
      {
        id: 'gpc_banner_mode',
        label: 'GPC banner mode',
        kind: 'one',
        options: [
          { label: 'Passive', clause: band('gpc_banner_mode', 'passive') },
          { label: 'Banner', clause: band('gpc_banner_mode', 'banner') },
          { label: 'Hidden', clause: band('gpc_banner_mode', 'hidden') },
          { label: 'Not in config', clause: band('gpc_banner_mode', 'unset') },
        ],
        hint: 'how the banner reacts when a GPC signal arrives',
      },
      {
        id: 'google_consent_mode',
        label: 'Google Consent Mode',
        kind: 'one',
        options: SETTING('google_consent_mode', {
          on: 'the real Google Consent Mode setting — not the plugin reporting a Google tracker, which is a separate row further down',
        }),
      },
      {
        id: 'microsoft_consent_mode',
        label: 'Microsoft Consent Mode',
        kind: 'one',
        options: SETTING('microsoft_consent_mode'),
      },
      {
        id: 'facebook_consent_mode',
        label: 'Facebook Consent Mode',
        kind: 'one',
        options: SETTING('facebook_consent_mode'),
      },
      {
        id: 'do_not_track',
        label: 'Do Not Track',
        kind: 'one',
        options: SETTING('do_not_track', {
          on: 'honours the browser Do Not Track header',
        }),
      },
      {
        id: 'ui_blocking',
        label: 'Blocking UI',
        kind: 'one',
        options: SETTING('ui_blocking', {
          on: 'the visitor cannot use the site until they choose',
        }),
      },
      {
        id: 'geolocation_enabled',
        label: 'Geolocation',
        kind: 'one',
        options: SETTING('geolocation_enabled', {
          on: 'the banner varies by where the visitor is',
        }),
      },
      {
        id: 'geolocation_method',
        label: 'Geolocation by',
        kind: 'one',
        options: [
          { label: 'Automatic', clause: band('geolocation_method', 'automatic') },
          { label: 'Manual', clause: band('geolocation_method', 'manual') },
          { label: 'Not in config', clause: band('geolocation_method', 'unset') },
        ],
      },
      {
        id: 'revoke_method',
        label: 'Revoke by',
        kind: 'one',
        options: [
          { label: 'Automatic', clause: band('revoke_method', 'automatic') },
          { label: 'Manual', clause: band('revoke_method', 'manual') },
          { label: 'Not in config', clause: band('revoke_method', 'unset') },
        ],
      },
      {
        id: 'multilanguage_mode',
        label: 'Language follows',
        kind: 'one',
        options: [
          { label: 'The site', clause: band('multilanguage_mode', 'site') },
          { label: 'The browser', clause: band('multilanguage_mode', 'browser') },
          { label: 'Not in config', clause: band('multilanguage_mode', 'unset') },
        ],
      },
      {
        id: 'consent_level',
        label: 'Consent level',
        kind: 'many',
        options: [
          { label: '1', clause: band('consent_level', '1'), hint: 'the norm — 240,512 banners' },
          { label: '2', clause: band('consent_level', '2') },
          { label: '3', clause: band('consent_level', '3') },
          { label: 'Not in config', clause: band('consent_level', 'unset') },
        ],
      },
      {
        id: 'consent_choices',
        label: 'Buttons',
        kind: 'one',
        options: [
          { label: 'Two', clause: band('consent_choices', '2') },
          { label: 'Three', clause: band('consent_choices', '3') },
          { label: 'Not in config', clause: band('consent_choices', 'unset') },
        ],
      },
      {
        id: 'banner_published',
        label: 'Banner published',
        kind: 'one',
        options: [
          { label: 'Yes', clause: band('banner_published', 'yes') },
          {
            label: 'Never',
            clause: band('banner_published', 'no'),
            hint: 'no design row at all — 37,619 live apps. A different absence from a missing setting, which reads "Not in config" above',
          },
        ],
      },
    ],
  },
  {
    title: 'Which regulations is it set for?',
    facets: [
      { id: 'gdpr_mode', label: 'GDPR', kind: 'one', options: REGULATION('gdpr_mode') },
      { id: 'ccpa_mode', label: 'CCPA', kind: 'one', options: REGULATION('ccpa_mode') },
      { id: 'ukpecr_mode', label: 'UK PECR', kind: 'one', options: REGULATION('ukpecr_mode') },
      { id: 'lgpd_mode', label: 'LGPD (Brazil)', kind: 'one', options: REGULATION('lgpd_mode') },
      { id: 'pipeda_mode', label: 'PIPEDA (Canada)', kind: 'one', options: REGULATION('pipeda_mode') },
      { id: 'popia_mode', label: 'POPIA (South Africa)', kind: 'one', options: REGULATION('popia_mode') },
      { id: 'otherus_mode', label: 'Other US states', kind: 'one', options: REGULATION('otherus_mode') },
    ],
    // The two config shapes are nearly disjoint: the newer one nests these
    // under `regulations`, the older carried only gdpr and ccpa at the top
    // level. Read newer-first with the old pair as fallback, which is why
    // LGPD and the rest read "Not in config" for most of the base.
  },
  {
    // THE PLUGIN'S SELF-REPORT, which is a different thing from the banner
    // configuration above: this is the WordPress plugin describing its own
    // environment, and only 15% of live apps have ever posted one. So "not
    // reported" is the normal answer here and is offered as a chip rather
    // than hidden behind an empty result. Useful for environment problems —
    // a caching plugin fighting the banner, WP-Cron disabled — and NOT for
    // what the banner does, which the config groups above answer properly.
    title: 'What the WordPress plugin reports',
    facets: [
      {
        id: 'consent_api',
        label: 'Consent API',
        kind: 'one',
        options: CONFIG_OPTIONS('consent_api'),
      },
      {
        id: 'script_blocking',
        label: 'Script blocking',
        kind: 'one',
        options: CONFIG_OPTIONS('script_blocking'),
        hint: 'blocking is what makes a banner enforce a choice rather than only record it',
      },
      {
        id: 'bot_detection',
        label: 'Bot detection',
        kind: 'one',
        options: CONFIG_OPTIONS('bot_detection'),
      },
      {
        id: 'google_tracking',
        label: 'Google tracker',
        kind: 'one',
        options: CONFIG_OPTIONS('google_tracking', {
          on: 'a Google tracker integration was reported — this is NOT Google Consent Mode, '
            + 'which nothing in the banner config mentions anywhere in the base',
        }),
      },
      {
        id: 'facebook_tracking',
        label: 'Facebook tracker',
        kind: 'one',
        options: CONFIG_OPTIONS('facebook_tracking'),
      },
      {
        id: 'speed_optimizer',
        label: 'Caching plugin',
        kind: 'one',
        options: CONFIG_OPTIONS('speed_optimizer', {
          on: 'litespeed, wp-rocket, autoptimize and seven others — aggressive caching is a '
            + 'common cause of a banner appearing not to work',
        }),
      },
      {
        id: 'wp_cron',
        label: 'WP-Cron',
        kind: 'one',
        options: [
          { label: 'Working', clause: band('wp_cron', 'ok') },
          { label: 'Disabled', clause: band('wp_cron', 'disabled') },
          { label: 'Not reported', clause: band('wp_cron', 'unknown') },
        ],
      },
      {
        id: 'banner_ui',
        label: 'Banner UI',
        kind: 'one',
        options: [
          { label: 'Legacy', clause: band('banner_ui', 'legacy') },
          { label: 'React', clause: band('banner_ui', 'react') },
          { label: 'Not reported', clause: band('banner_ui', 'unknown') },
        ],
      },
      {
        id: 'conditional_loading',
        label: 'Conditional loading',
        kind: 'one',
        options: CONFIG_OPTIONS('conditional_loading'),
      },
      {
        id: 'multisite',
        label: 'WP multisite',
        kind: 'one',
        options: CONFIG_OPTIONS('multisite'),
        hint: 'one install serving many sites — not the same as the portfolio count below',
      },
      {
        id: 'amp_support',
        label: 'AMP',
        kind: 'one',
        options: CONFIG_OPTIONS('amp_support'),
      },
      {
        id: 'banner_platform',
        label: 'Platform',
        kind: 'one',
        options: [
          { label: 'WordPress', clause: band('banner_platform', 'wordpress') },
          { label: 'Other', clause: band('banner_platform', 'other') },
          { label: 'Not reported', clause: band('banner_platform', 'unknown') },
        ],
      },
      {
        id: 'config_freshness',
        label: 'Config reported',
        kind: 'one',
        options: AGE_OPTIONS('days_since_config_report'),
        hint: 'a setting is only as true as the last report — pair this with any row above '
          + 'before writing copy that claims what their banner does',
      },
      range('config_between', 'Config reported between', 'config_reported_on'),
    ],
  },
  {
    title: 'Who are they?',
    facets: [
      {
        id: 'portfolio',
        label: 'Sites',
        kind: 'many',
        options: [
          { label: 'One', clause: band('portfolio', 'single') },
          { label: 'A few', clause: band('portfolio', 'multi') },
          { label: 'Agency (5+)', clause: band('portfolio', 'agency') },
        ],
      },
      {
        id: 'gdpr',
        label: 'EU exposure',
        kind: 'many',
        options: [
          { label: 'High', clause: band('gdpr_exposure', 'high') },
          { label: 'Mixed', clause: band('gdpr_exposure', 'mixed') },
          { label: 'Low', clause: band('gdpr_exposure', 'low') },
        ],
        hint: 'visitor geography outranks where the company is registered',
      },
    ],
  },
]

/**
 * Could any chip show this clause?
 *
 * Used to decide whether Advanced has to be open: a saved requirement no chip
 * represents would otherwise be invisible, with every chip reading `any` —
 * which says the filter has no requirements when it plainly has some.
 *
 * A `many` band facet folds several values into one `in` clause, so a match is
 * checked by NAME for bands rather than by serialised form. `traffic_band in
 * (dead, low)` is representable even though no single chip equals it.
 */
/* `serialize` is PASSED IN rather than duplicated. This module had its own
   copy of the serialiser to avoid importing the builder, and the copy promptly
   fell behind when the date clause was added — reporting every date
   requirement as unrepresentable. One serialiser, handed over. */
export function representableAsChip(
  clause: Clause,
  serialize: (c: Clause) => string,
): boolean {
  // BY VALUE FOR BANDS, not by name. Matching on the name alone reported
  // `engagement_recency in (dormant, gone)` as representable because ONE chip
  // somewhere mentioned `engagement_recency` — so g5's requirement lit no chip
  // and did not open Advanced either. It was simply invisible, which is the
  // failure this function exists to rule out.
  if (clause.kind === 'band') {
    // `!=` is not something any chip emits, so it stays advanced-only.
    if (clause.op === '!=') return false
    const offered = new Set<string>()
    for (const group of FACET_GROUPS) {
      for (const facet of group.facets) {
        for (const option of facet.options) {
          if (option.clause.kind !== 'band' || option.clause.name !== clause.name) continue
          for (const v of option.clause.values) offered.add(v)
        }
      }
    }
    return clause.values.every((v) => offered.has(v))
  }

  const wanted = serialize(clause)
  for (const group of FACET_GROUPS) {
    for (const facet of group.facets) {
      for (const option of facet.options) {
        if (serialize(option.clause) === wanted) return true
      }
    }
  }
  return false
}

/**
 * Is this option's clause present in the condition?
 *
 * Compared by SERIALISED FORM, which is the only comparison that cannot
 * disagree with what gets saved. A chip that lit up for a clause that
 * serialises differently would be claiming to have added something else.
 */
export function optionIsOn(option: FacetOption, clauses: Clause[], serialize: (c: Clause) => string): boolean {
  const wanted = serialize(option.clause)
  return clauses.some((c) => serialize(c) === wanted)
}

/**
 * Facet options this vocabulary cannot express, so a chip is never offered for
 * a name or value the engine does not have.
 *
 * Belt and braces: the chips are hand-written against the vocabulary as it is
 * today, and `variables.yaml` is editable. A renamed band would otherwise give
 * a chip that saves a condition the API rejects — or worse, one that parses and
 * matches nobody.
 */
export function unavailable(option: FacetOption, vocab: VocabularyEntry[]): boolean {
  const entry = vocab.find((v) => v.name === option.clause.name)
  if (!entry) return true
  if (option.clause.kind === 'band') {
    if (entry.kind !== 'band') return true
    return option.clause.values.some((v) => !entry.values?.some((b) => b.value === v))
  }
  if (option.clause.kind === 'number') return entry.kind !== 'number'
  if (option.clause.kind === 'date') return entry.kind !== 'date'
  return entry.kind !== 'flag'
}

/** A range row is unusable if its date signal is not in the vocabulary. */
export function rangeUnavailable(facet: Facet, vocab: VocabularyEntry[]): boolean {
  const entry = vocab.find((v) => v.name === facet.field)
  return !entry || entry.kind !== 'date'
}
